<script setup>
import { ref, computed, watchEffect, watch } from 'vue';
import { API } from '@/services/index.js';
import { usePermissionsStore } from '@/stores/permissions.store.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useUserStore } from '@/stores/user.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { useSettingsStore } from '@/stores/settings.store.js';
import { GET_WORKSPACE_TEAM_MEMBERS_QUERY } from '@/services/workspace/queries/workspace/index.js';
import { GRANT_INVITE_WORKSPACE_USER_ROLE, REVOKE_ALL_WORKSPACE_USER_ROLES } from '@/services/workspace/mutations/workspace/index.js';
import { GET_ENUMS_QUERY } from '@/services/common/queries.js';
import AddWorkspaceTeamMemberModal from '@/pages/settings/workspace/components/addWorkspaceTeamMemberModal.vue';
import BaseUserAvatar from '@/components/BaseUserAvatar.vue';
import partnrUpShortLogo from '@/assets/logo/PartnrUP_P_logo.png';
import { format } from 'date-fns';

const queryClient = useQueryClient();
const permissions = usePermissionsStore();
const isUpdatingPermissions = computed(() => useUserStore().isUpdatingPermissions);
const activeAccount = computed(() => useSettingsStore().activeAccount);
const activeWorkspace = computed(() => useSettingsStore().activeWorkspace);
const isUpdatingMemberWorkspaces = ref(false);
const teamMembers = ref([]);
const searchedTeamMember = ref('');
const removeMemberConfirmationDialog = ref({
  isOpen: false,
  user: null,
  isRemoving: false
});
const headers = [
  { title: 'Collaborators', key: 'user', width: '350' },
  { title: '', key: 'projects', width: '1000px' },
  { title: 'Last Login', key: 'lastLogin', width: '200' },
  { title: 'Verified', key: 'verified', width: '200' }
];

watchEffect(async () => {
  if (!activeWorkspace.value) {
    teamMembers.value = [];
    await queryClient.removeQueries({ queryKey: 'loadWorkspaceTeamMembers' });
  } else {
    await queryClient.invalidateQueries({ queryKey: ['loadWorkspaceTeamMembers'] });
  }
});

const { isLoading, isPending, isFetching, error, data } = useQuery({
  queryKey: ['loadWorkspaceTeamMembers', activeWorkspace.value?.id],
  // enabled: !!activeWorkspace.value?.id,
  queryFn: async () => {
    const payload = {
      workspace_id: Number(activeWorkspace.value.id)
    };
    const res = await API.sendQuery('/graphql/workspace', GET_WORKSPACE_TEAM_MEMBERS_QUERY, payload);
    if (res.data.data.teamMembers) {
      teamMembers.value = res.data.data.teamMembers.map((member) => {
        return {
          ...member,
          isUpdatingMemberRole: false,
          editRoles: false,
          editProject: false,
          showMoreProjects: false,
          isRemovingProject: {
            project_id: null,
            isLoading: false
          }
        };
      });
      return res.data.data.teamMembers;
    }
    //
    if (res.data.errors[0].message === 'Unauthorized') {
      teamMembers.value = [];
      return [];
    }
  }
});

const {
  isLoading: isLoadingAccountRoles,
  isPending: isPendingAccountRoles,
  isFetching: isFetchingAccountRoles,
  data: accountRoles
} = useQuery({
  queryKey: ['loadWorkspaceRoles'],
  queryFn: async () => {
    const payload = {
      name: 'ACLRoleEnum'
    };
    const res = await API.sendQuery('/graphql', GET_ENUMS_QUERY, payload);
    if (res.data.data.getEnums) {
      const visibleAccountRoleTypes = ['Workspace_Admin', 'Workspace_Editor', 'Workspace_Client', 'Workspace_Watcher'];
      return res.data.data.getEnums
        .filter((role) => {
          return visibleAccountRoleTypes.includes(role);
        })
        .map((role) => {
          let name = role.replaceAll('_', ' ');
          return {
            id: role,
            name: name
          };
        });
    }
  },
  refetchOnWindowFocus: false
});

const filteredTeamMembers = computed(() => {
  const mappedTeamMembers = [...teamMembers.value];
  return mappedTeamMembers.filter((data) => {
    if (searchedTeamMember.value === '' || searchedTeamMember.value === null || searchedTeamMember.value.length === 0) {
      return teamMembers.value;
    }

    if (data.user?.first_name) {
      return data.user.first_name.toLowerCase().includes(searchedTeamMember.value.toLowerCase());
    }

    if (data.user?.email) {
      return data.user.email.toLowerCase().includes(searchedTeamMember.value.toLowerCase());
    }
    return teamMembers.value;
  });
});

const updateUserWorkspaceRole = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/workspace', GRANT_INVITE_WORKSPACE_USER_ROLE, payload);
  },
  onSuccess: async (data, payload) => {
    teamMembers.value[payload.index].isUpdatingMemberRole = false;
    if (data.data.data.grantUserRole) {
      // await queryClient.invalidateQueries({ queryKey: ['loadWorkspaceTeamMembers'] });
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
  }
});

const updateMemberRolesHandler = async (member, index) => {
  teamMembers.value[index].isUpdatingMemberRole = true;

  const payload = {
    account_id: Number(activeAccount.value.id),
    workspace_ids: [Number(activeWorkspace.value.id)],
    user_id: Number(member.user.id),
    role: member.role.name,
    member: member,
    index: index
  };

  await updateUserWorkspaceRole.mutateAsync(payload);
};

const revokeWorkspaceUserRoleHandler = async (index, member, projectID, projectRole) => {
  // console.log(member, workspaceID);
  // teamMembers.value[index].isRemovingWorkspace = {
  //   workspace_id: workspaceID,
  //   isLoading: true
  // };
  // const payload = {
  //   workspace_id: Number(workspaceID),
  //   user_id: Number(member.user.id),
  //   role: workspaceRole,
  //   member: member,
  //   memberIndex: index
  // };
  // await revokeWorkspaceUserRole.mutateAsync(payload);
};

const manageProject = async (project) => {};

const removeUserFromWorkspace = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/workspace', REVOKE_ALL_WORKSPACE_USER_ROLES, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.revokeAllUserRoles) {
      removeMemberConfirmationDialog.value = {
        isRemoving: false,
        isOpen: false,
        user: null
      };
      await queryClient.invalidateQueries({ queryKey: ['loadWorkspaceTeamMembers'] });
    }

    if (data.data.error) {
      console.log(data.data.error);
      removeMemberConfirmationDialog.value.isRemoving = false;
    }
  },
  onError: (error) => {
    console.log('error', error);
    removeMemberConfirmationDialog.value.isRemoving = false;
  }
});

const openRemoveMemberConfirmationDialog = (user) => {
  removeMemberConfirmationDialog.value = {
    isOpen: true,
    user: user,
    isRemoving: false
  };
};

const removeMemberFromAccountHandle = async () => {
  removeMemberConfirmationDialog.value.isRemoving = true;
  const payload = {
    workspace_id: Number(activeWorkspace.value.id),
    user_id: removeMemberConfirmationDialog.value.user.id
  };

  await removeUserFromWorkspace.mutateAsync(payload);
};
</script>

<template>
  <div class="tw-max-w-full lg:tw-max-w-[750px]">
    <div class="tw-mb-5 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between lg:tw-flex-row lg:tw-space-x-10">
      <div class="tw-flex tw-w-full tw-items-center">
        <v-text-field
          v-model="searchedTeamMember"
          hide-details
          density="compact"
          variant="solo"
          placeholder="Search for a collaborator"
          append-inner-icon="mdi-magnify"
          clearable
          height="37"
          :disabled="!activeWorkspace"
        ></v-text-field>
      </div>
      <div class="tw-mt-5 tw-flex tw-w-full tw-justify-start tw-space-x-2 lg:tw-mt-0 lg:tw-justify-end">
        <AddWorkspaceTeamMemberModal
          v-if="permissions.canAddWorkspaceTeamMember()"
          :account-roles="accountRoles"
          :is-disabled="isLoading || !activeWorkspace"
        />
      </div>
    </div>
  </div>
  <div class="tw-mb-[4rem] tw-h-full lg:tw-h-[600px]">
    <v-data-table-virtual
      :headers="headers"
      :items="filteredTeamMembers"
      :loading="isLoading || isFetching"
      :loading-text="isLoading || isFetching ? 'Loading collaborators...' : 'No collaborators found'"
      items-per-page="-1"
      hide-default-footer
      :fixed-header="true"
      :sticky="true"
      height="600"
      density="compact"
      item-key="user"
    >
      <template v-slot:item="{ item, index }">
        <tr v-if="item.user">
          <td>
            <div class="tw-my-6 tw-flex tw-items-start tw-space-x-3">
              <BaseUserAvatar class="tw-mr-2" :text-styles="`cursor: pointer;font-size: 1.688rem; line-height: 64px;`" :size="50" :user="item.user" />
              <div class="tw-flex tw-flex-col tw-space-y-1">
                <template v-if="item.user.first_name || (item.user.username && item.user.username.length > 0)">
                  <div class="tw-text tw-flex tw-space-x-2 tw-font-semibold">
                    <span>{{ item.user.username.length > 0 ? item.user.username : `${item.user.first_name} ${item.user.last_name}` }}</span>
                    <!--                    <span v-if="isOwnerAndAuthUser(item)" class="tw-text-[0.8rem] tw-font-bold tw-italic tw-text-[#ff5b9f]">(You)</span>-->
                  </div>
                </template>
                <div class="tw-text-sm tw-font-medium tw-text-[#979797]">{{ item.user.email }}</div>
                <div v-if="item.role.name !== 'Owner'">
                  <v-select
                    class="user-role-select"
                    v-model="item.role.name"
                    variant="solo"
                    density="compact"
                    item-title="name"
                    item-value="id"
                    :disabled="isUpdatingMemberWorkspaces || item.isUpdatingMemberRole"
                    :loading="item.isUpdatingMemberRole"
                    :items="accountRoles"
                    @update:modelValue="updateMemberRolesHandler(item, index)"
                    min-width="180"
                    width="180"
                    hide-details
                  >
                    <template v-slot:append-item>
                      <v-divider class="mb-2"></v-divider>
                      <v-list-item style="color: red" title="Remove" @click="openRemoveMemberConfirmationDialog(item.user)"> </v-list-item>
                    </template>
                  </v-select>

                  <v-dialog
                    v-if="removeMemberConfirmationDialog.user?.id === item.user.id"
                    v-model="removeMemberConfirmationDialog.isOpen"
                    @update:modelValue="removeMemberConfirmationDialog.isOpen"
                    width="auto"
                    attach="#app"
                    :persistent="removeMemberConfirmationDialog.isRemoving"
                  >
                    <v-card class="tw-rounded-xl" max-width="497" width="497" :loading="removeMemberConfirmationDialog.isRemoving">
                      <div class="tw-px-5 tw-pb-5">
                        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">
                          Remove Team Member
                        </h2>
                        <div class="tw-rounded-xl tw-p-3">
                          <div class="tw-flex tw-w-full tw-items-center">
                            <BaseUserAvatar
                              style="margin: 0 auto"
                              :text-styles="`cursor: pointer;font-size: 1.688rem; line-height: 64px;`"
                              :size="75"
                              :user="item.user"
                            />
                          </div>
                          <div class="tw-my-5 tw-text-center tw-text-[0.87rem] tw-font-semibold">
                            Are you sure you want to remove your team member from this workspace? This will also remove them from all projects.
                          </div>
                          <div class="tw-mt-7 tw-flex tw-items-center tw-justify-between">
                            <v-btn
                              :disabled="removeMemberConfirmationDialog.isRemoving"
                              class="tw-capitalize"
                              @click="removeMemberConfirmationDialog.isOpen = false"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              :loading="removeMemberConfirmationDialog.isRemoving"
                              :disabled="removeMemberConfirmationDialog.isRemoving"
                              class="tw-capitalize"
                              color="red"
                              @click="removeMemberFromAccountHandle"
                            >
                              Remove
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </v-card>
                  </v-dialog>
                </div>
                <div v-if="item.role.name === 'Owner'">
                  <div class="tw-text tw-font-semibold" :class="item.role.name === 'Owner' ? 'tw-text-[#e37f06]' : 'tw-text-primary-blue'">
                    {{ item.role.name }}
                  </div>
                </div>
              </div>
            </div>
          </td>
          <td>
            <v-table class="inner-table tw-my-4">
              <tr>
                <th class="text-left tw-w-[1000px]">Projects</th>
                <th class="text-left tw-w-[303px]">Invited By</th>
                <th class="text-left tw-w-[303px]">Invited Date</th>
                <th class="text-left tw-w-[303px]"></th>
              </tr>
              <tbody>
                <template v-if="item.projects[0].applies_to_all">
                  <tr>
                    <td>
                      <div class="tw-flex tw-items-center tw-space-x-2">
                        <v-btn width="43" height="43" size="x-small" density="compact" color="primary-blue">
                          <v-icon class="tw-text-[1.8rem]" icon="mdi-earth"></v-icon>
                        </v-btn>
                        <div class="tw-font-semibold">ALL</div>
                      </div>
                    </td>
                    <td>
                      <div class="invited-by-avatar" v-if="item.projects[0].invited_by">
                        <BaseUserAvatar class="tw-mb-1" :size="40" :user="item.projects[0].invited_by" />
                        <v-tooltip attach="#app" activator="parent" location="start">
                          {{ item.projects[0].invited_by.username || item.projects[0].invited_by.first_name }}
                        </v-tooltip>
                      </div>
                    </td>
                    <td>
                      <div class="tw-font-medium">
                        {{ item.projects[0]?.invited_date ? format(item.projects[0].invited_date, 'PPP') : '' }}
                      </div>
                    </td>
                    <td>
                      <v-hover v-slot="{ isHovering, props }">
                        <v-btn
                          v-bind="props"
                          v-if="item.role.name !== 'Owner' && item.role.name !== 'Admin'"
                          :color="isHovering ? 'red' : 'blue-grey-lighten-4'"
                          variant="text"
                          density="compact"
                          icon="mdi-delete"
                          :loading="item.isRemovingProject.isLoading"
                          :disabled="item.isRemovingProject.isLoading"
                          @click="() => console.log('Removing user from this project.')"
                        ></v-btn>
                        <v-tooltip attach="#app" activator="parent" location="start" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold">
                          Remove From All Projects
                        </v-tooltip>
                      </v-hover>
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <template v-for="(project, projectIndex) in item.projects" :key="project.id">
                    <template v-if="project">
                      <tr v-if="!item.showMoreProjects ? projectIndex <= 2 : projectIndex >= 0">
                        <td>
                          <div v-if="project.project" class="tw-flex tw-items-center tw-space-x-2">
                            <div
                              class="tw-group tw-flex tw-cursor-pointer tw-items-center tw-space-x-2 tw-space-y-2"
                              @click="manageProject(project.project)"
                            >
                              <div>
                                <v-avatar v-if="project.project.image" size="35">
                                  <v-img sizes="35" contain :src="project.project.image" :alt="`${project.project.name} logo`"></v-img>
                                </v-avatar>
                                <v-avatar v-else :rounded="false" size="35">
                                  <v-img sizes="35" contain :src="partnrUpShortLogo" :alt="`default no-logo`"></v-img>
                                </v-avatar>
                              </div>
                              <div>
                                <div class="tw-flex tw-flex-col">
                                  <div class="tw-font-semibold group-hover:tw-text-primary-blue">
                                    {{ project.project.name }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <template v-if="project.invited_by">
                            <div class="invited-by-avatar">
                              <BaseUserAvatar class="tw-mb-1" :size="40" :user="project.invited_by" />
                              <v-tooltip
                                attach="#app"
                                activator="parent"
                                location="start"
                                content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold"
                              >
                                {{ project.invited_by.username || project.invited_by.first_name }}
                              </v-tooltip>
                            </div>
                          </template>
                        </td>
                        <td>
                          <div class="tw-font-medium">
                            {{ project && project.invited_date ? format(project.invited_date, 'PPP') : '' }}
                          </div>
                        </td>
                        <td>
                          <v-hover v-slot="{ isHovering, props }" v-if="project.project">
                            <v-btn
                              v-bind="props"
                              :color="isHovering ? 'red' : 'blue-grey-lighten-4'"
                              variant="text"
                              density="compact"
                              icon="mdi-delete"
                              :loading="item.isRemovingProject.project_id === project.project.id && item.isRemovingProject.isLoading"
                              :disabled="item.isRemovingProject.project_id === project.project.id && item.isRemovingProject.isLoading"
                              @click="
                                item.projects.length === 1
                                  ? openRemoveMemberConfirmationDialog(item.user)
                                  : revokeWorkspaceUserRoleHandler(index, item, project.project.id, project.role.name)
                              "
                            ></v-btn>
                            <v-tooltip
                              attach="#app"
                              activator="parent"
                              location="start"
                              content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold"
                            >
                              Remove Project Access
                            </v-tooltip>
                          </v-hover>
                        </td>
                      </tr>
                    </template>
                  </template>
                  <div class="tw-mt-2">
                    <v-btn
                      density="compact"
                      variant="text"
                      class="tw-mt-2 tw-text-[0.79rem] tw-font-bold tw-capitalize"
                      style="padding-left: 0; padding-right: 0"
                      color="primary-blue"
                      @click="item.showMoreProjects = !item.showMoreProjects"
                      v-if="item.projects.length > 3"
                    >
                      {{ item.showMoreProjects ? 'See less' : 'See All' }}
                    </v-btn>
                  </div>
                </template>
              </tbody>
            </v-table>
          </td>

          <td>
            <div class="tw-font-medium">
              {{ item.user.last_login ? format(item.user.last_login, 'PPP') : '' }}
            </div>
          </td>
          <td>
            <div class="tw-font-medium">
              <v-chip
                :model-value="item.user.verified"
                class="ma-2"
                color="teal"
                prepend-icon="mdi-checkbox-marked-circle"
                density="comfortable"
                size="small"
              >
                {{ item.user.verified ? 'Verified' : 'Not Verified' }}
              </v-chip>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table-virtual>
  </div>
</template>

<style scoped lang="css">
#partnrup .v-table.inner-table td {
  @apply tw-border-b-0 tw-pl-0;
}
</style>
